import React, { useState, useCallback, useEffect } from 'react';
import { Upload, Button, Image, Space, message } from 'antd';
import { UploadOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload/interface';
import axios from 'axios';

interface FileUploadFieldProps {
  fieldName: string;
  label: string;
  value?: string; // File ID
  onChange: (fileId: string | undefined, uploadedAt?: string) => void;
  placeholder?: string;
  disabled?: boolean;
  accept?: string;
  maxSize?: number; // in MB
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  fieldName,
  label,
  value,
  onChange,
  placeholder = 'Click to upload',
  disabled = false,
  accept = 'image/*',
  maxSize = 5, // 5MB default
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>();

  // Load preview URL if file ID exists
  useEffect(() => {
    if (value) {
      setPreviewUrl(`/api/files/preview/documents/${value}`);
    } else {
      setPreviewUrl(undefined);
    }
  }, [value]);

  const handleUpload = useCallback(async (file: RcFile) => {
    // Validate file size
    const fileSizeMB = file.size / 1024 / 1024;
    if (fileSizeMB > maxSize) {
      message.error(`File size must be less than ${maxSize}MB`);
      return false;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Determine category based on field name
      let category = 'CUSTOMER_DOCUMENT';
      let documentType = 'FILE';
      
      if (fieldName === 'photoFileId') {
        category = 'CUSTOMER_PHOTO';
        documentType = 'PHOTO';
      } else if (fieldName === 'signatureFileId') {
        category = 'CUSTOMER_SIGNATURE';
        documentType = 'SIGNATURE';
      }
      
      const metadata = {
        category,
        domain: 'CUSTOMER',
        documentType,
        description: `${label} for customer`,
      };
      formData.append('metadata', JSON.stringify(metadata));

      const response = await axios.post('/api/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const fileId = response.data.id;
      const uploadedAt = new Date().toISOString();
      
      onChange(fileId, uploadedAt);
      message.success(`${label} uploaded successfully`);
    } catch (error) {
      console.error(`${label} upload error:`, error);
      message.error(`Failed to upload ${label}`);
    } finally {
      setUploading(false);
    }
    
    return false; // Prevent default upload
  }, [fieldName, label, maxSize, onChange]);

  const handleRemove = () => {
    onChange(undefined, undefined);
    message.info(`${label} removed`);
  };

  return (
    <div className="file-upload-field">
      {previewUrl ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ 
            border: '1px solid #d9d9d9', 
            borderRadius: 8, 
            padding: 16,
            textAlign: 'center',
            backgroundColor: '#fafafa'
          }}>
            <Image
              src={previewUrl}
              alt={label}
              style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
              preview={{
                mask: <EyeOutlined style={{ fontSize: 20 }} />
              }}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
            />
          </div>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Upload
              accept={accept}
              showUploadList={false}
              beforeUpload={handleUpload}
              disabled={disabled || uploading}
            >
              <Button
                icon={<UploadOutlined />}
                loading={uploading}
                disabled={disabled}
              >
                Replace
              </Button>
            </Upload>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
              disabled={disabled}
            >
              Remove
            </Button>
          </Space>
        </Space>
      ) : (
        <Upload
          accept={accept}
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={disabled || uploading}
        >
          <Button
            icon={<UploadOutlined />}
            loading={uploading}
            disabled={disabled}
            block
          >
            {uploading ? 'Uploading...' : placeholder}
          </Button>
        </Upload>
      )}
    </div>
  );
};

export default FileUploadField;
